import React, { useEffect, useState } from "react";
import { useGSAPReveal } from "../util/useGSAPReveal";

export const AboutMe = () => {
  const [isMobileView, setIsMobileView] = useState(false);
  const [isCompactView, setIsCompactView] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const headerRef = useGSAPReveal({ variant: "fade-up" });
  const portraitRef = useGSAPReveal({ variant: "fade-up", delay: 0.08 });
  const contentRef = useGSAPReveal({ variant: "stagger-children", delay: 0.16, staggerAmount: 0.08 });

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobileView(window.innerWidth <= 768);
      setIsCompactView(window.innerWidth <= 980);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const detailsVisible = !isCompactView || isExpanded;

  return (
    <section
      id="about"
      aria-label="About Me"
      style={{
        minHeight: "100vh",
        background: "transparent",
        padding: isMobileView ? "96px 24px 76px" : "148px 80px 100px",
        fontFamily: "'Inter', sans-serif",
        position: "relative",
        overflow: "visible",
        zIndex: 2,
      }}
    >
      <div style={{ maxWidth: "1180px", margin: "0 auto", position: "relative", zIndex: 2 }}>
        <div ref={headerRef} style={{ marginBottom: isMobileView ? "42px" : "60px" }}>
          <div
            style={{
              width: "48px",
              height: "3px",
              background: "#C4956A",
              borderRadius: "2px",
              marginBottom: "22px",
            }}
          />
          <h2
            style={{
              fontSize: isMobileView ? "42px" : "64px",
              fontWeight: 600,
              fontFamily: "'Space Grotesk', sans-serif",
              color: "#F5F5F5",
              letterSpacing: "-0.03em",
              lineHeight: 0.98,
              marginBottom: "12px",
            }}
          >
            About
          </h2>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isCompactView ? "1fr" : "320px minmax(0, 1fr)",
            gap: isCompactView ? "36px" : "88px",
            alignItems: "start",
          }}
        >
          <div ref={portraitRef}>
            <div
              style={{
                position: "relative",
                width: isCompactView ? (isMobileView ? "250px" : "320px") : "100%",
                maxWidth: "100%",
                aspectRatio: "0.8",
                margin: isCompactView ? "0 auto" : "0",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  inset: "-18px auto auto -18px",
                  width: "120px",
                  height: "120px",
                  borderTop: "2px solid rgba(196, 149, 106, 0.65)",
                  borderLeft: "2px solid rgba(196, 149, 106, 0.65)",
                  borderTopLeftRadius: "28px",
                  pointerEvents: "none",
                }}
              />
              <div
                style={{
                  position: "absolute",
                  inset: "auto -18px -18px auto",
                  width: "120px",
                  height: "120px",
                  borderBottom: "2px solid rgba(196, 149, 106, 0.45)",
                  borderRight: "2px solid rgba(196, 149, 106, 0.45)",
                  borderBottomRightRadius: "28px",
                  pointerEvents: "none",
                }}
              />
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  overflow: "hidden",
                  borderRadius: "34px",
                  border: "1px solid rgba(245, 245, 245, 0.08)",
                  background: "#111111",
                  boxShadow: "0 30px 70px rgba(0, 0, 0, 0.32)",
                }}
              >
                <img
                  src="/asim.jpg"
                  alt="Asim Hafeez — Engineering Lead and System Architect"
                  loading="eager"
                  decoding="async"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                  }}
                />
              </div>
            </div>

          </div>

          <div ref={contentRef}>
            <div
              style={{
                marginBottom: "30px",
                paddingBottom: "28px",
                borderBottom: "1px solid rgba(245, 245, 245, 0.08)",
              }}
            >
              <div
                style={{
                  color: "rgba(196, 149, 106, 0.92)",
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: "11px",
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  marginBottom: "14px",
                }}
              >
                Asim Hafeez
              </div>
              <div
                style={{
                  color: "rgba(245, 245, 245, 0.56)",
                  fontSize: isMobileView ? "15px" : "16px",
                  lineHeight: 1.6,
                  marginBottom: "16px",
                }}
              >
                Engineering Lead, Senior Software Engineer, and systems-minded builder.
              </div>
              <p
                style={{
                  fontSize: isMobileView ? "24px" : "clamp(34px, 4vw, 48px)",
                  lineHeight: 1.2,
                  color: "#F2F2F2",
                  letterSpacing: "-0.04em",
                  maxWidth: "780px",
                }}
              >
                I build secure, scalable systems and lead engineering work from architecture to execution.
              </p>
            </div>

            <div style={{ display: "grid", gap: "24px", maxWidth: "820px" }}>
              <p
                style={{
                  fontSize: isMobileView ? "16px" : "18px",
                  lineHeight: 1.9,
                  color: "#A3A3A3",
                  fontWeight: 300,
                }}
              >
                I’m <span style={{ color: "#F5F5F5", fontWeight: 500 }}>Asim Hafeez</span>, currently leading backend engineering at <span style={{ color: "#F5F5F5", fontWeight: 500 }}>Evert</span> in Norway and pursuing a <span style={{ color: "#F5F5F5", fontWeight: 500 }}>Micro Masters in Statistics & Data Science at MIT</span>. My work has moved across Web2, blockchain, cloud architecture, and now AI engineering, always centered on building reliable systems that hold up under real usage.
              </p>

              {detailsVisible && (
                <p
                  style={{
                    fontSize: isMobileView ? "16px" : "18px",
                    lineHeight: 1.9,
                    color: "#A3A3A3",
                    fontWeight: 300,
                  }}
                >
                  I’ve worked in high-growth startups across fintech and SaaS, led 0-to-1 product development, built agentic systems with LangChain and LangGraph, and write regularly through <span style={{ color: "#F5F5F5", fontWeight: 500 }}>AI Focus</span>. I’m also the author of <em style={{ color: "#C4956A", fontStyle: "italic" }}>"Designing Scalable Systems"</em>.
                </p>
              )}

              {isCompactView && (
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    width: "fit-content",
                    background: "transparent",
                    border: "none",
                    borderBottom: "1px solid rgba(196, 149, 106, 0.24)",
                    color: "#C4956A",
                    fontSize: "14px",
                    fontWeight: 500,
                    cursor: "pointer",
                    padding: 0,
                    fontFamily: "'Inter', sans-serif",
                  }}
                >
                  <span>{isExpanded ? "Show less" : "Read more"}</span>
                  <span style={{ transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.3s ease" }}>⌄</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
